import { useEffect, useState } from "react";

import { getErrorMessage } from "@/core/services";
import { useAsyncList, useLastKnownTotal, useTableState } from "@/ds";
import type { TableSort } from "@/ds";
import {
  COTACOES_REFERENCIA_SORT_FIELDS,
  listarCotacoesReferencia,
} from "@/modules/financeiro-configuracoes";
import type {
  CotacaoReferenciaItem,
  FiltroSituacaoCotacao,
  ListarCotacoesReferenciaParams,
  OrdenarCotacoesReferenciaPor,
} from "@/modules/financeiro-configuracoes";
import {
  MOEDAS_ERROR_MESSAGE,
  MOEDAS_FORBIDDEN_MESSAGE,
  MOEDA_TODAS,
  SITUACAO_PADRAO,
} from "../moedas.constants";

const ORDEM_PADRAO: TableSort<OrdenarCotacoesReferenciaPor> = {
  field: "cadastradaEm",
  direction: "desc",
};

const isSortField = (value: unknown): value is OrdenarCotacoesReferenciaPor =>
  COTACOES_REFERENCIA_SORT_FIELDS.some((campo) => campo === value);

export function useMoedasLista() {
  const [moedaId, setMoedaId] = useState(MOEDA_TODAS);
  const [situacao, setSituacao] = useState<FiltroSituacaoCotacao>(SITUACAO_PADRAO);

  const { lastTotal, remember } = useLastKnownTotal();
  const tabela = useTableState<OrdenarCotacoesReferenciaPor>({
    isSortField,
    defaultSort: ORDEM_PADRAO,
    total: lastTotal,
    resetKey: JSON.stringify([moedaId, situacao]),
  });
  const sort = tabela.sort ?? ORDEM_PADRAO;

  const lista = useAsyncList<ListarCotacoesReferenciaParams, CotacaoReferenciaItem>({
    fetcher: (params, signal) => listarCotacoesReferencia(params, { signal }),
    params: {
      moedaId: moedaId === MOEDA_TODAS ? undefined : moedaId,
      situacao,
      page: tabela.page + 1,
      pageSize: tabela.rows,
      sort,
    },
    forbiddenMessage: MOEDAS_FORBIDDEN_MESSAGE,
    errorMessage: MOEDAS_ERROR_MESSAGE,
    resolveErrorMessage: getErrorMessage,
  });
  remember(lista.status, lista.total);

  const vazioAtivas =
    lista.status === "success" &&
    lista.total === 0 &&
    moedaId === MOEDA_TODAS &&
    situacao === "ATIVAS";
  const [temCotacoes, setTemCotacoes] = useState<boolean | null>(null);
  useEffect(() => {
    if (!vazioAtivas) return;
    const controller = new AbortController();
    setTemCotacoes(null);
    listarCotacoesReferencia(
      { situacao: "TODAS", page: 1, pageSize: 1 },
      { signal: controller.signal },
    )
      .then(({ total }) => setTemCotacoes(total > 0))
      .catch(() => {
        if (!controller.signal.aborted) setTemCotacoes(true);
      });
    return () => controller.abort();
  }, [vazioAtivas, lista.items]);
  const verificandoVazio = vazioAtivas && temCotacoes === null;
  const semCotacoes =
    lista.status === "success" &&
    lista.total === 0 &&
    moedaId === MOEDA_TODAS &&
    (situacao === "TODAS" || (vazioAtivas && temCotacoes === false));

  return {
    moedaId,
    situacao,
    setMoedaId,
    setSituacao,
    tabela,
    items: lista.items,
    total: lista.total,
    status: lista.status,
    errorMessage: lista.errorMessage,
    reload: lista.reload,
    semCotacoes,
    verificandoVazio,
    limparFiltros: () => {
      setMoedaId(MOEDA_TODAS);
      setSituacao("TODAS");
    },
  };
}
